import * as React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import { useQuery } from "@tanstack/react-query";
import { fetchArticles } from "../../../services/tecdocService";
import { Article } from "../../../interfaces/Article";
import ProductItem from "../../products/ProductItem";
import LocalProductItem from "../../products/LocalProductItem";
import theme from "../../../theme";

type Props = {
  keyword: string;
  linkageTargetId?: number | null;
  linkageTargetType?: string | null;
};

const OemTecdocResults = ({
  keyword,
  linkageTargetId,
  linkageTargetType,
}: Props) => {
  const [comparisonArticles, setComparisonArticles] = React.useState<Article[]>(
    []
  );

  const { data: articlesData, isLoading } = useQuery({
    queryKey: [
      "oem-tecdoc-articles",
      keyword,
      linkageTargetType,
      linkageTargetId,
    ],
    queryFn: () =>
      fetchArticles(keyword, {
        linkageTargetType,
        linkageTargetId,
        page: 1,
      }),
    enabled: !!keyword,
  });

  const updateComparisonArticles = React.useCallback(
    (article: Article, isChecked: boolean) => {
      setComparisonArticles((prev) =>
        isChecked
          ? [...prev, article]
          : prev.filter((a) => a !== article)
      );
    },
    []
  );

  const renderProductItem = React.useCallback(
    (index: number) => {
      if (!articlesData?.articles || index >= articlesData.articles.length) {
        return null;
      }
      const article = articlesData.articles[index];
      return article.tec_doc_article ? (
        <ProductItem
          key={`${article.articleNumber}-${index}`}
          article={article}
          onToggle={updateComparisonArticles}
        />
      ) : (
        <LocalProductItem
          key={`${article.articleNumber}-${index}`}
          article={article}
        />
      );
    },
    [articlesData?.articles, updateComparisonArticles]
  );

  if (!keyword) return null;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 1.5,
          py: 3,
          mt: 2,
          border: "1px solid #eee",
          borderRadius: 1,
          bgcolor: "#fafafa",
        }}
      >
        <CircularProgress size={28} />
        <Typography
          variant="body2"
          sx={{ fontFamily: "Inter", color: theme.palette.text.primary }}
        >
          Loading TecDoc articles for {keyword}…
        </Typography>
      </Box>
    );
  }

  if (!articlesData?.articles?.length) {
    return (
      <Typography
        variant="h6"
        textAlign="center"
        sx={{ mt: 4, fontFamily: "Inter", color: theme.palette.text.primary }}
      >
        No products found for {keyword}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          bgcolor: "#f1f1f1",
          py: 1,
          px: 2,
          borderTop: `2px solid ${theme.palette.primary.dark}`,
          mb: 2,
        }}
      >
        <Typography
          variant="body1"
          sx={{ fontFamily: "Inter", fontWeight: 600, fontSize: 14 }}
        >
          TecDoc articles for {keyword}
          {comparisonArticles.length > 0
            ? ` · ${comparisonArticles.length} selected`
            : ""}
        </Typography>
      </Box>
      <Virtuoso
        useWindowScroll
        style={{ height: "calc(100vh - 200px)", width: "100%" }}
        totalCount={articlesData.articles.length}
        itemContent={renderProductItem}
        overscan={400}
        increaseViewportBy={{ top: 400, bottom: 400 }}
        defaultItemHeight={200}
      />
    </Box>
  );
};

export default OemTecdocResults;
